import { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

interface Language {
  code: string;
  name: string;
  isDefault: boolean;
}

interface LanguageSelectorProps {
  languages?: Language[];
  defaultLanguage?: Language;
  onChange?: (language: Language) => void;
  disabled?: boolean; // Add this prop
}

export default function LanguageSelector({
  languages = [],
  defaultLanguage,
  onChange,
  disabled = false, // Default to false
}: LanguageSelectorProps) {
  // Fall back to hardcoded languages if none provided (for backward compatibility)
  const availableLanguages =
    languages.length > 0
      ? languages
      : [
          { code: 'en', name: 'English', isDefault: true },
          //   { code: 'id', name: 'Bahasa Indonesia', isDefault: false },
        ];

  // Initialize with default language if provided, otherwise use first default language or first item
  const getInitialLanguage = () => {
    if (defaultLanguage) return defaultLanguage;
    const defaultLang = availableLanguages.find((lang) => lang.isDefault);
    return defaultLang || availableLanguages[0];
  };

  const [selectedLanguage, setSelectedLanguage] = useState<Language>(
    getInitialLanguage(),
  );
  const [isOpen, setIsOpen] = useState(false);

  // Update selected language when defaultLanguage prop changes
  useEffect(() => {
    if (defaultLanguage) {
      setSelectedLanguage(defaultLanguage);
    }
  }, [defaultLanguage]);

  // Handle language selection
  const handleLanguageSelect = (lang: Language) => {
    setSelectedLanguage(lang);
    setIsOpen(false);
    if (onChange) {
      onChange(lang);
    }
  };

  return (
    <div className="w-[16rem] max-w-md">
      <label
        htmlFor="language-select"
        className="block text-[#161618] text-[14px] font-[700] mb-2"
      >
        Roleplay language
      </label>

      <div className="relative">
        <button
          id="language-select"
          type="button"
          className={`flex items-center justify-between w-full px-6 py-4 text-left bg-white border border-[#c7cbce] rounded-full text-[#161618] focus:outline-none focus:ring-2 focus:ring-[#1C7AEB] transition-colors ${
            disabled ? 'opacity-60 cursor-not-allowed' : ''
          }`}
          onClick={() => !disabled && setIsOpen(!isOpen)} // Only toggle if not disabled
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          disabled={disabled}
        >
          <span>{selectedLanguage.name}</span>
          <ChevronDown
            className={`w-5 h-5 ${
              isOpen ? 'text-[#1C7AEB]' : 'text-[#707173]'
            } transition-colors`}
          />
        </button>

        {isOpen && !disabled && (
          <div className="absolute z-10 w-full mt-2 bg-white rounded-3xl shadow-lg border border-[#d9dde0]/30">
            <ul
              className="py-3 overflow-auto max-h-60"
              role="listbox"
              aria-labelledby="language-select"
            >
              {availableLanguages.map((lang, index) => (
                <li key={lang.code}>
                  <button
                    className={`w-full px-6 py-3 text-left transition-colors ${
                      selectedLanguage.code === lang.code
                        ? 'text-[#1C7AEB] font-medium'
                        : 'text-[#161618] hover:text-[#1C7AEB]'
                    }`}
                    role="option"
                    aria-selected={selectedLanguage.code === lang.code}
                    onClick={() => handleLanguageSelect(lang)}
                  >
                    {lang.name}
                  </button>
                  {index < availableLanguages.length - 1 && (
                    <div className="mx-6 border-t border-[#d9dde0]"></div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
